import React, { useContext, useEffect, useState } from "react";
import PolygonIcon from "../../utils/svg/poligon-icon";
import OnewayIcon from "../../utils/svg/oneway-icon";
import RoundIcon from "../../utils/svg/round-icon";
import TrashIcon from "../../utils/svg/trash-icon";
import DownArrowIcon from "../../utils/svg/down-arrow-icon";
import CrossIcon from "../../utils/svg/cross-icon";
import MinusIcon from "../../utils/svg/minus-dash-icon";
import QuestionIcon from "../../utils/svg/question-icon";
import { ONE_WAY, ROUND_TRIP, STAND_ALONE } from "../../utils/consts";
import WarningIcon from "../../utils/svg/warning-icon";
import PortSearchField from "./shared/items/port-search-field";
import { getQuote, minutesToHours } from "../../utils/FlexxAPI";
import MobileLeg from "./shared/items/mobile-leg";
import GettingStartedContext from "../../utils/GettingStartedContext";
import DesktopLeg from "./shared/items/desktop-leg";

const FlightCalculator = ({
    type,
    planeModel,
    embedded,
    setRecommended,
    setFlightCalculatorOpened,
    widgetKey,
}) => {
    const elementTitles = document.querySelector("[data-title]");
    const [opened, setOpened] = useState(type === STAND_ALONE);
    const [lock, setLock] = useState(null);
    const [from, setFrom] = useState(null);
    const [to, setTo] = useState(null);
    const [tripType, setTripType] = useState(ONE_WAY);
    const [estimate, setEstimate] = useState(null);
    const [fuelStop, setFuelStop] = useState(null);
    const [fuelStopPorts, setFuelStopPorts] = useState([]);
    const [totalEstimate, setTotalEstimate] = useState(0);
    const [additional, setAdditional] = useState(null);
    const [trips, setTrips] = useState(1);
    const [legs, setLegs] = useState([]);
    const [abortController, setAbortController] = useState(null);
    const [grandTotal, setGrandTotal] = useState(0);
    const [plane, setPlane] = useState(
        elementTitles ? elementTitles.getAttribute("data-default-plane") : ""
    );
    const [cacheLoad, setCacheLoad] = useState(false);
    const [loading, setLoading] = useState(false);

    const {
        langString,
        data,
        showToolTip,
        showWarningToolTip,
        requestVariable,
    } = useContext(GettingStartedContext);

    useEffect(() => {
        getCosts();
    }, [from, to, tripType, plane]);

    useEffect(() => {
        setTotalEstimate((estimate + additional) * trips);
    }, [estimate, additional, trips]);

    useEffect(() => {
        let total = 0;
        for (const leg of legs) {
            total += parseFloat(leg.total);
        }
        setGrandTotal(total);
        if (widgetKey && legs.length && !cacheLoad) {
            const cache = {
                plane: plane,
                legs: legs,
            };
            // window.localStorage.setItem(widgetKey, JSON.stringify(cache))
            window.localStorage.setItem("ftc-cache", JSON.stringify(cache));
        } else if (cacheLoad) {
            setCacheLoad(false);
        }
    }, [legs]);

    useEffect(() => {
        recalculateLegs();
    }, [plane]);

    useEffect(() => {
        if (plane !== planeModel && planeModel) {
            setPlane(planeModel);
        }
    }, [planeModel]);

    useEffect(() => {
        if (setRecommended && grandTotal > 0) {
            setRecommended(grandTotal);
        }
    }, [grandTotal]);

    useEffect(() => {
        if (
            requestVariable("aircraft") &&
            requestVariable("ownership") &&
            requestVariable("hours") &&
            requestVariable("plan")
        ) {
            setPlane(data.Options.Planes[0].ModelID);
        }
        if (type === STAND_ALONE) {
            //setPlane(data.Options.Planes[0].ModelID);
        }
        // load from cache
        if (widgetKey) {
            // const json = window.localStorage.getItem(widgetKey);
            const json = window.localStorage.getItem("ftc-cache");
            if (json) {
                const cache = JSON.parse(json);
                if (cache && cache.plane && cache.legs.length) {
                    setCacheLoad(true);
                    setLegs(cache.legs);
                    if (type === STAND_ALONE) {
                        setPlane(cache.plane);
                    }
                    // if (cache.plane !== plane) {
                    //   recalculateLegs();
                    // }
                }
            }
        }
    }, []);

    const recalculateLegs = async () => {
        if (legs.length) {
            setLock(true);
            const newLegs = [];
            for (const leg of legs) {
                leg.estimate = "Loading";
            }
            for (const leg of legs) {
                try {
                    const response = await getQuote(
                        leg.from,
                        leg.to,
                        leg.tripType,
                        plane,
                        null
                    );
                    if (response.error === false) {
                        const newLeg = leg;
                        newLeg.estimate = response.flight_time;
                        newLeg.additional = response.block_time;
                        newLeg.fuel_stop = response.fuel_stop;
                        newLeg.fuel_stop_ports = response.fuel_stop_ports;
                        newLeg.plane = plane;
                        newLeg.total =
                            (response.flight_time + response.block_time) *
                            leg.trips;
                        newLegs.push(newLeg);
                    }
                } catch (e) {
                    console.log(e);
                }
            }
            setLock(false);
            setLegs(newLegs);
        }
    };

    const getCosts = () => {
        if (abortController) {
            abortController.abort();
        }
        const ac = new AbortController();
        setAbortController(ac);
        if (from && to && plane) {
            setLoading(true);
            try {
                getQuote(from, to, tripType, plane, ac.signal).then(
                    (response) => {
                        if (response.error) {
                            return;
                        }
                        setEstimate(response.flight_time);
                        setAdditional(response.block_time);
                        setFuelStop(response.fuel_stop);
                        setFuelStopPorts(response.fuel_stop_ports);
                        setLoading(false);
                    }
                );
            } catch (e) {
                setLoading(false);
            }
        } else {
            setEstimate(0);
            setAdditional(0);
        }
    };

    const handleAddTrip = () => {
        if (from && to && tripType && trips && estimate) {
            setLegs([
                ...legs,
                {
                    from: from,
                    to: to,
                    tripType: tripType,
                    trips: trips,
                    estimate: estimate,
                    additional: additional,
                    total: totalEstimate,
                    plane: plane,
                    fuel_stop_ports: fuelStopPorts,
                    fuel_stop: fuelStop,
                },
            ]);
            setFrom(null);
            setTo(null);
            setFuelStop(false);
            setFuelStopPorts([]);
            setTripType(ONE_WAY);
            setTrips(1);
            setEstimate(0);
            setAdditional(0);
        }
    };

    const deleteLeg = (index) => {
        legs.splice(index, 1);
        setLegs([...legs]);
    };

    const handleTripUpdate = (leg, index) => {
        const newLegs = [...legs];
        newLegs[index] = leg;
        setLegs(newLegs);
    };

    const title = elementTitles
        ? elementTitles.getAttribute("data-title")
        : null;
    const dropdownLabel = elementTitles
        ? elementTitles.getAttribute("data-dropdown-label")
        : null;
    const subtitle = elementTitles
        ? elementTitles.getAttribute("data-subtitle")
        : null;
    const disclaimer = elementTitles
        ? elementTitles.getAttribute("data-disclaimer")
        : null;
    const exploreBtnTitle = elementTitles
        ? elementTitles.getAttribute("data-explore-btn-title")
        : null;
    const exploreBtnURL = elementTitles
        ? elementTitles.getAttribute("data-explore-btn-url")
        : null;
    const defaultPlane = elementTitles
        ? elementTitles.getAttribute("data-default-plane")
        : null;

    let mainClass = "flight-calculator";
    if (embedded) {
        mainClass += " flight-calculator--embedded";
    }
    if (lock) {
        mainClass += " flight-calculator--locked";
    }

    if (opened) {
        mainClass += " flight-calculator--opened";
    }

    return (
        <>
            <div className={mainClass}>
                <div
                    className={`grid-x ${
                        opened ? "flight-calculator__wrapper" : ""
                    }`}
                >
                    {type !== STAND_ALONE && (
                        <div className="small-12 flight-calculator__start text-center">
                            <h6>{langString("NOT_SURE_HOW_MUCH_YOU_FLY")}</h6>
                            <button
                                className="button secondary btn-pill"
                                onClick={(e) => {
                                    e.preventDefault();
                                    const openOrClose = !opened;
                                    setOpened(openOrClose);
                                    if (setFlightCalculatorOpened) {
                                        setFlightCalculatorOpened(openOrClose);
                                    }
                                }}
                            >
                                {langString("FLIGHT_TIME_CALCULATOR")}
                                <span>
                                    {opened ? <MinusIcon /> : <CrossIcon />}
                                </span>
                            </button>
                        </div>
                    )}
                    <div
                        className="small-12 large-11 flight-calculator__body"
                        style={{ display: opened ? "block" : "none" }}
                    >
                        <div className="flight-calculator__form">
                            {type === STAND_ALONE && (
                                <div className="small-12 medium-9 flight-calculator__heading text-center">
                                    <h3>{title}</h3>
                                    <div
                                        className="flight-calculator__heading__summery"
                                        dangerouslySetInnerHTML={{
                                            __html: subtitle,
                                        }}
                                    />
                                    <label className="dropdown_label">
                                        {dropdownLabel}{" "}
                                    </label>
                                    <div className="flight-calculator__chooser">
                                        <select
                                            onChange={(e) => {
                                                setPlane(e.target.value);
                                            }}
                                            defaultValue={plane}
                                        >
                                            {data.Options.Planes.map(
                                                (option, index) => (
                                                    <option
                                                        key={index}
                                                        value={option.ModelID}
                                                    >
                                                        {option.Title}
                                                    </option>
                                                )
                                            )}
                                        </select>
                                    </div>
                                </div>
                            )}

                            {type !== STAND_ALONE && (
                                <div className="flight-calculator__form__title">
                                    <p>
                                        {langString(
                                            "FLIGHT_CALCULATOR_FORM_TITLE"
                                        )}
                                    </p>
                                </div>
                            )}

                            <div className="flight-calculator__form__content--desktop">
                                <table>
                                    <thead>
                                        <tr className="flight-calculator__form__topics">
                                            <th></th>
                                            <th></th>
                                            <th></th>
                                            {/* <th></th> */}
                                            <th
                                                colSpan="3"
                                                className="flight-calculator__trip__annual text-center"
                                            >
                                                <b>{langString("ANNUAL")}</b>
                                            </th>
                                        </tr>
                                        <tr>
                                            <th></th>

                                            <th className="text-center">
                                                <div className="flight-calculator__trip__title">
                                                    <p>
                                                        {langString(
                                                            "ONE_WAY_OR_ROUND_TRIP"
                                                        )}
                                                    </p>
                                                    <PolygonIcon />
                                                </div>
                                            </th>
                                            <th></th>
                                            <th className="flight-calculator__trip__time flight-calculator__number text-center padding-bottom hrs-trips">
                                                {langString("HOURS_PER_TRIP")}
                                            </th>
                                            <th className="text-center padding-bottom hrs-trips">
                                                {langString("NOF_TRIPS")}
                                            </th>
                                            <th className="text-center flight-calculator__number padding-bottom hrs-trips">
                                                {langString("TOTAL_HRS")}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {legs.length > 0 && (
                                            <>
                                                {legs.map((leg, index) => {
                                                    return (
                                                        <DesktopLeg
                                                            key={index}
                                                            index={index}
                                                            leg={leg}
                                                            onDelete={() => {
                                                                deleteLeg(
                                                                    index
                                                                );
                                                            }}
                                                            onUpdate={
                                                                handleTripUpdate
                                                            }
                                                        />
                                                    );
                                                })}
                                            </>
                                        )}
                                        <tr>
                                            <td className="flight-calculator__inputs">
                                                <div className="flight-calculator__input">
                                                    {
                                                        <PortSearchField
                                                            name={"from"}
                                                            placeholder={langString(
                                                                "FLIGHT_FROM"
                                                            )}
                                                            val={
                                                                from
                                                                    ? from.iata
                                                                        ? from.iata
                                                                        : from.name
                                                                    : ""
                                                            }
                                                            onSelect={(val) => {
                                                                setFrom(val);
                                                            }}
                                                        />
                                                    }
                                                </div>
                                            </td>
                                            <td>
                                                <div className="flight-calculator__trip">
                                                    <div className="flight-calculator__trip__content">
                                                        <div
                                                            className={`flight-calculator__trip__button ${
                                                                tripType ===
                                                                ONE_WAY
                                                                    ? "active"
                                                                    : ""
                                                            }`}
                                                            onClick={() => {
                                                                setTripType(
                                                                    ONE_WAY
                                                                );
                                                            }}
                                                        >
                                                            <OnewayIcon />
                                                        </div>
                                                        <div
                                                            className={`flight-calculator__trip__button ${
                                                                tripType ===
                                                                ROUND_TRIP
                                                                    ? "active"
                                                                    : ""
                                                            }`}
                                                            onClick={() => {
                                                                setTripType(
                                                                    ROUND_TRIP
                                                                );
                                                            }}
                                                        >
                                                            <RoundIcon />
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="flight-calculator__input">
                                                    {
                                                        <PortSearchField
                                                            name={"to"}
                                                            placeholder={langString(
                                                                "FLIGHT_TO"
                                                            )}
                                                            val={
                                                                to
                                                                    ? to.iata
                                                                        ? to.iata
                                                                        : to.name
                                                                    : ""
                                                            }
                                                            onSelect={(val) => {
                                                                setTo(val);
                                                            }}
                                                        />
                                                    }
                                                </div>

                                                {!!fuelStop && (
                                                    <div className="flight-calculator__warning">
                                                        <WarningIcon />
                                                        <p>
                                                            Fuel stop(s)
                                                            required
                                                        </p>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="time1">
                                                {/* {minutesToHours(estimate, 1)} */}
                                                {loading
                                                    ? "Loading.."
                                                    : minutesToHours(
                                                          estimate,
                                                          1
                                                      )}
                                            </td>
                                            <td>
                                                <input
                                                    type="number"
                                                    min={1}
                                                    value={trips}
                                                    onChange={(e) => {
                                                        setTrips(
                                                            e.target.value <= 0
                                                                ? 0
                                                                : e.target.value
                                                        );
                                                    }}
                                                />
                                            </td>
                                            <td className="time2">
                                                {/* {minutesToHours(estimate, trips)} */}
                                                {loading
                                                    ? "Loading.."
                                                    : minutesToHours(
                                                          estimate,
                                                          trips
                                                      )}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            {legs.length > 0 && (
                                <>
                                    {legs.map((leg, index) => {
                                        return (
                                            <MobileLeg
                                                key={index}
                                                index={index}
                                                leg={leg}
                                                onDelete={() => {
                                                    deleteLeg(index);
                                                }}
                                                onUpdate={handleTripUpdate}
                                            />
                                        );
                                    })}
                                </>
                            )}

                            <div className="flight-calculator__form__content--mobile">
                                <div className="flight-calculator__trip-data">
                                    <div className="flight-calculator__way">
                                        <div className="flight-calculator__inputs">
                                            <div>
                                                {
                                                    <PortSearchField
                                                        name={"from"}
                                                        placeholder={langString(
                                                            "FLIGHT_FROM"
                                                        )}
                                                        val={
                                                            from
                                                                ? from.iata
                                                                    ? from.iata
                                                                    : from.name
                                                                : ""
                                                        }
                                                        onSelect={(val) => {
                                                            setFrom(val);
                                                        }}
                                                    />
                                                }
                                            </div>
                                            <div>
                                                {
                                                    <PortSearchField
                                                        name={"to"}
                                                        placeholder={langString(
                                                            "FLIGHT_TO"
                                                        )}
                                                        val={
                                                            to
                                                                ? to.iata
                                                                    ? to.iata
                                                                    : to.name
                                                                : ""
                                                        }
                                                        onSelect={(val) => {
                                                            setTo(val);
                                                        }}
                                                    />
                                                }
                                            </div>
                                        </div>
                                        {!!fuelStop && (
                                            <div className="flight-calculator__warning">
                                                <WarningIcon />
                                                <p>Fuel stop(s) required</p>
                                            </div>
                                        )}
                                    </div>
                                    <div className="flight-calculator__trip">
                                        <div className="flight-calculator__trip__title">
                                            <p>
                                                {langString(
                                                    "ONE_WAY_OR_ROUND_TRIP"
                                                )}
                                            </p>
                                        </div>
                                        <div className="flight-calculator__trip__content">
                                            <div
                                                onClick={() => {
                                                    setTripType(ONE_WAY);
                                                }}
                                                className={`flight-calculator__trip__button  ${
                                                    tripType === ONE_WAY
                                                        ? "active"
                                                        : ""
                                                }`}
                                            >
                                                <OnewayIcon />
                                            </div>
                                            <div
                                                onClick={() => {
                                                    setTripType(ROUND_TRIP);
                                                }}
                                                className={`flight-calculator__trip__button  ${
                                                    tripType === ROUND_TRIP
                                                        ? "active"
                                                        : ""
                                                }`}
                                            >
                                                <RoundIcon />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="flight-calculator__time">
                                    <div className="flight-calculator__hours">
                                        <p>
                                            {minutesToHours(estimate, 1)}{" "}
                                            <span>/trip</span>
                                        </p>
                                    </div>
                                    <div className="flight-calculator__trip-amount">
                                        <input
                                            type="number"
                                            min={1}
                                            value={trips}
                                            onChange={(e) => {
                                                setTrips(
                                                    e.target.value <= 0
                                                        ? 0
                                                        : e.target.value
                                                );
                                            }}
                                        />
                                        <p>
                                            <b># of Trips</b>
                                        </p>
                                    </div>
                                    <div className="flight-calculator__hours">
                                        <p>
                                            {minutesToHours(estimate, trips)}{" "}
                                            <span>annually</span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flight-calculator__footer grid-x">
                            <div className="text-left flight-calculator__add-button">
                                <button
                                    className={`button ${
                                        from &&
                                        to &&
                                        tripType &&
                                        trips &&
                                        estimate
                                            ? "primary"
                                            : "disabled"
                                    }`}
                                    onClick={handleAddTrip}
                                >
                                    {langString("ADD_TRIP")}
                                    <span>
                                        <CrossIcon />
                                    </span>
                                </button>
                            </div>
                            {!!legs.length > 0 && (
                                <div className="flight-calculator__end">
                                    <div className="flight-calculator__end__note">
                                        {/*<div className="flight-calculator__end__note__icon">*/}
                                        {/*  <QuestionIcon />*/}
                                        {/*</div>*/}
                                        {showWarningToolTip(
                                            "CALCULATIONS_DISCLAIMER"
                                        )}
                                        <p>{disclaimer}</p>
                                        <h6 className={"text-right"}>
                                            {langString("ANNUAL_TOTAL")}{" "}
                                            <span>
                                                {minutesToHours(
                                                    grandTotal +
                                                        estimate * trips,
                                                    1
                                                )}
                                            </span>
                                        </h6>
                                    </div>
                                </div>
                            )}
                        </div>
                        {!!exploreBtnTitle && (
                            <div className="flight-calculator__footer__end">
                                <a
                                    className="button primary"
                                    href={exploreBtnURL}
                                    target="_blank"
                                >
                                    {exploreBtnTitle}
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default FlightCalculator;
